import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, CheckIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import DateTimePicker from './DateTimePicker';

interface AccessRequest {
  id: string;
  file_id: string;
  user_id: string;
  request_type: 'access_count_limit' | 'access_period_limit' | 'asset_permissions';
  status: 'pending' | 'accepted' | 'rejected' | 'canceled';
  current_times_accessible?: number;
  current_total_accesses: number;
  current_allowed_access_to?: string;
  requested_times_accessible?: number;
  requested_allowed_access_to?: string;
  requested_asset_permissions?: any;
  requester_message?: string;
  reason?: string;
  created_at: string;
  responded_at?: string;
  responded_by?: string;
  response_message?: string;
  file_name?: string;
  requester_email?: string;
  requester_name?: string;
  owner_id?: string;
  owner_email?: string;
  // File recipient information (if available from backend)
  shared_at?: string;
  recipient_allowed_access_to?: string;
}

interface AccessRequestResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  fileId?: string;
  notificationId?: string; // Optional notification ID to mark as read after responding
  onResponded?: () => void; // Callback when request is responded to
}

const AccessRequestResponseModal: React.FC<AccessRequestResponseModalProps> = ({
  isOpen,
  onClose,
  requestId,
  fileId,
  notificationId,
  onResponded
}) => {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<AccessRequest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [responseStatus, setResponseStatus] = useState<'accepted' | 'rejected'>('accepted');
  const [responseMessage, setResponseMessage] = useState('');
  const [overrideTimesAccessible, setOverrideTimesAccessible] = useState<string>('');
  const [overrideAllowedAccessTo, setOverrideAllowedAccessTo] = useState<string>('');
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [isUnlimitedPeriod, setIsUnlimitedPeriod] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const loadRequestDetails = useCallback(async () => {
    if (!requestId) return;
    
    setIsLoading(true);
    try {
      const detailedRequest = await apiService.getAccessRequest(requestId);
      setSelectedRequest(detailedRequest);
      // Reset form
      setResponseStatus('accepted');
      setResponseMessage('');
      setOverrideAllowedAccessTo('');
      setValidationError('');
      
      // Set default value to current_times_accessible (or empty if unlimited)
      if (detailedRequest.current_times_accessible !== null && detailedRequest.current_times_accessible !== undefined) {
        setOverrideTimesAccessible(detailedRequest.current_times_accessible.toString());
        setIsUnlimited(false);
      } else {
        setOverrideTimesAccessible('');
        setIsUnlimited(true);
      }
      
      // Set default value for access period limit
      if (detailedRequest.current_allowed_access_to) {
        // Convert to yyyy-mm-dd hh:mm:ss format for display
        const date = new Date(detailedRequest.current_allowed_access_to);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        setOverrideAllowedAccessTo(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);
        setIsUnlimitedPeriod(false);
      } else {
        setOverrideAllowedAccessTo('');
        setIsUnlimitedPeriod(true);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load request details');
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [requestId, onClose]);

  // Load request details when modal opens
  useEffect(() => {
    if (isOpen && requestId) {
      loadRequestDetails();
    } else {
      // Reset form when closing
      setSelectedRequest(null);
      setResponseStatus('accepted');
      setResponseMessage('');
      setOverrideTimesAccessible('');
      setOverrideAllowedAccessTo('');
      setIsUnlimited(false);
      setIsUnlimitedPeriod(false);
      setValidationError('');
    }
  }, [isOpen, requestId, loadRequestDetails]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isResponding) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, isResponding, onClose]);

  const handleRespond = async (status: 'accepted' | 'rejected') => {
    if (!selectedRequest) return;

    // Validate times_accessible if accepting and not unlimited
    if (status === 'accepted' && selectedRequest.request_type === 'access_count_limit' && !isUnlimited) {
      const newValue = parseInt(overrideTimesAccessible);
      if (isNaN(newValue)) {
        setValidationError('Please enter a valid number');
        return;
      }
      
      // Check if current_times_accessible exists (not unlimited)
      if (selectedRequest.current_times_accessible !== null && selectedRequest.current_times_accessible !== undefined) {
        // Must be greater than the existing limit
        if (newValue <= selectedRequest.current_times_accessible) {
          setValidationError(`Value must be greater than ${selectedRequest.current_times_accessible} (current limit)`);
          return;
        }
      } else {
        // Currently unlimited, so any positive number is valid
        if (newValue <= 0) {
          setValidationError('Value must be greater than 0');
          return;
        }
      }
      setValidationError('');
    }

    setIsResponding(true);
    try {
      let timesAccessible: number | null | undefined = undefined;
      let allowedAccessTo: string | null | undefined = undefined;

      // If accepting an access_count_limit request
      if (status === 'accepted' && selectedRequest.request_type === 'access_count_limit') {
        if (isUnlimited || !overrideTimesAccessible) {
          // Unlimited - set to null
          timesAccessible = null;
        } else {
          timesAccessible = parseInt(overrideTimesAccessible);
        }
      } else if (status === 'accepted' && selectedRequest.request_type === 'access_period_limit') {
        // For access_period_limit, convert yyyy-mm-dd hh:mm:ss to RFC3339 format
        if (isUnlimitedPeriod) {
          // Unlimited - set to null
          allowedAccessTo = null;
        } else if (overrideAllowedAccessTo) {
          // Format: "2029-10-17 18:39:00" (local time, no timezone)
          // Convert to RFC3339: "2029-10-17T18:39:00Z" (UTC)
          // Replace space with T and add Z for UTC
          const dateTimeStr = overrideAllowedAccessTo.replace(' ', 'T');
          const date = new Date(dateTimeStr);
          if (isNaN(date.getTime())) {
            throw new Error('Invalid date format. Please use yyyy-mm-dd hh:mm:ss');
          }
          // Convert to ISO string (UTC) which is RFC3339 format
          allowedAccessTo = date.toISOString();
        } else if (selectedRequest.requested_allowed_access_to) {
          // Use requested value if no override - already in ISO format from backend
          allowedAccessTo = new Date(selectedRequest.requested_allowed_access_to).toISOString();
        }
      }

      console.log(`🎯 Responding to request ${selectedRequest.id} with status:`, status);
      
      await apiService.respondToAccessRequest(
        selectedRequest.id,
        status,
        responseMessage || undefined,
        selectedRequest.request_type === 'access_count_limit' ? timesAccessible : undefined,
        allowedAccessTo
      );

      toast.success(`Request ${status === 'accepted' ? 'accepted' : 'rejected'} successfully`);
      
      // Mark notification as read if notificationId is provided
      if (notificationId) {
        try {
          await apiService.markNotificationAsRead(notificationId);
        } catch (error) {
          console.error('Failed to mark notification as read:', error);
          // Don't fail the whole operation if marking as read fails
        }
      }
      
      // Refresh data BEFORE closing modal and calling callback
      // This ensures the status is updated immediately
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['access-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['access-requests-status'] }), // Invalidate status cache
        queryClient.invalidateQueries({ queryKey: ['notifications'] }),
      ]);
      
      // Refetch immediately to get fresh status
      await queryClient.refetchQueries({ queryKey: ['access-requests-status'] });
      
      // Call callback if provided
      if (onResponded) {
        onResponded();
      }
      
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to respond to request');
    } finally {
      setIsResponding(false);
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'access_count_limit':
        return 'Access Count Limit';
      case 'access_period_limit':
        return 'Access Period Limit';
      case 'asset_permissions':
        return 'Asset Permissions';
      default:
        return type;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'accepted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Accepted
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Rejected
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Canceled
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-[9999] backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Review Access Request</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : selectedRequest ? (
            <>
              {/* Summary card */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60 overflow-hidden">
                {/* File + requester row */}
                <div className="px-4 py-3 flex items-start gap-3">
                  <DocumentIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{selectedRequest.file_name || 'Unknown file'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Requested by {selectedRequest.requester_email || 'Unknown'}
                      {selectedRequest.requester_name ? ` · ${selectedRequest.requester_name}` : ''}
                    </p>
                    {selectedRequest.shared_at && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Shared {formatDate(selectedRequest.shared_at)}</p>
                    )}
                  </div>
                </div>

                {/* Request type + access info */}
                <div className="px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{getRequestTypeLabel(selectedRequest.request_type)}</p>
                  {selectedRequest.request_type === 'access_count_limit' ? (
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Current limit</span>
                        <p className="font-medium text-gray-900 dark:text-white mt-0.5">
                          {selectedRequest.current_times_accessible != null ? selectedRequest.current_times_accessible : 'Unlimited'}
                        </p>
                      </div>
                      <div className="text-gray-300 dark:text-gray-600">→</div>
                      {selectedRequest.requested_times_accessible != null && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Requested</span>
                          <p className="font-medium text-indigo-600 dark:text-indigo-400 mt-0.5">{selectedRequest.requested_times_accessible}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Used</span>
                        <p className="font-medium text-gray-900 dark:text-white mt-0.5">{selectedRequest.current_total_accesses}</p>
                      </div>
                    </div>
                  ) : selectedRequest.request_type === 'access_period_limit' ? (
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Access until</span>
                        <p className="font-medium text-gray-900 dark:text-white mt-0.5">
                          {selectedRequest.current_allowed_access_to ? formatDate(selectedRequest.current_allowed_access_to) : 'No limit'}
                        </p>
                      </div>
                      {selectedRequest.requested_allowed_access_to && (
                        <>
                          <div className="text-gray-300 dark:text-gray-600">→</div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Requested</span>
                            <p className="font-medium text-indigo-600 dark:text-indigo-400 mt-0.5">{formatDate(selectedRequest.requested_allowed_access_to)}</p>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700 dark:text-gray-300">Custom asset permissions requested</p>
                  )}
                </div>

                {/* Requester message */}
                {(selectedRequest.requester_message || selectedRequest.reason) && (
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/40">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Message</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedRequest.requester_message || selectedRequest.reason}</p>
                  </div>
                )}
              </div>

              {/* Response Form (only for pending requests) */}
              {selectedRequest.status === 'pending' && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                  {/* Override fields (only shown when accepting) */}
                  {responseStatus === 'accepted' && (
                    <>
                      {selectedRequest.request_type === 'access_count_limit' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Override Access Count (optional)
                          </label>
                          <div className="flex items-center gap-3">
                            <div className="w-1/3">
                              <input
                                type="number"
                                value={isUnlimited ? '' : overrideTimesAccessible}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setOverrideTimesAccessible(value);
                                  if (value) {
                                    setIsUnlimited(false);
                                    const numValue = parseInt(value);
                                    if (isNaN(numValue)) {
                                      setValidationError('Please enter a valid number');
                                    } else {
                                      // Check if current_times_accessible exists (not unlimited)
                                      if (selectedRequest.current_times_accessible !== null && selectedRequest.current_times_accessible !== undefined) {
                                        // Must be greater than the existing limit
                                        if (numValue <= selectedRequest.current_times_accessible) {
                                          setValidationError(`Value must be greater than ${selectedRequest.current_times_accessible} (current limit)`);
                                        } else {
                                          setValidationError('');
                                        }
                                      } else {
                                        // Currently unlimited, so any positive number is valid
                                        if (numValue <= 0) {
                                          setValidationError('Value must be greater than 0');
                                        } else {
                                          setValidationError('');
                                        }
                                      }
                                    }
                                  } else {
                                    setValidationError('');
                                  }
                                }}
                                placeholder={isUnlimited ? 'Unlimited' : ''}
                                disabled={isUnlimited}
                                min={selectedRequest.current_times_accessible !== null && selectedRequest.current_times_accessible !== undefined 
                                  ? selectedRequest.current_times_accessible + 1 
                                  : 1}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                              />
                              {validationError && (
                                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{validationError}</p>
                              )}
                              {!validationError && !isUnlimited && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {selectedRequest.current_times_accessible !== null && selectedRequest.current_times_accessible !== undefined
                                    ? `Must be greater than ${selectedRequest.current_times_accessible} (current limit)`
                                    : 'Must be greater than 0'}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Unlimited</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newUnlimited = !isUnlimited;
                                  setIsUnlimited(newUnlimited);
                                  if (newUnlimited) {
                                    setOverrideTimesAccessible('');
                                    setValidationError('');
                                  } else {
                                    // Restore to current_times_accessible when toggling off
                                    if (selectedRequest.current_times_accessible !== null && selectedRequest.current_times_accessible !== undefined) {
                                      setOverrideTimesAccessible(selectedRequest.current_times_accessible.toString());
                                    } else {
                                      setOverrideTimesAccessible('');
                                    }
                                  }
                                }}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                                  isUnlimited ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    isUnlimited ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedRequest.request_type === 'access_period_limit' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Override Access Until (optional)
                          </label>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 relative">
                              <DateTimePicker
                                value={isUnlimitedPeriod ? null : overrideAllowedAccessTo}
                                onChange={(value) => {
                                  if (value) {
                                    setOverrideAllowedAccessTo(value);
                                    setIsUnlimitedPeriod(false);
                                  } else {
                                    setOverrideAllowedAccessTo('');
                                    setIsUnlimitedPeriod(true);
                                  }
                                }}
                                placeholder={isUnlimitedPeriod || !overrideAllowedAccessTo ? 'Unlimited' : 'yyyy-mm-dd hh:mm:ss'}
                                disabled={isUnlimitedPeriod}
                                minDate={new Date()}
                                showTimeSelect={true}
                                timeIntervals={1}
                              />
                              {!isUnlimitedPeriod && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Format: yyyy-mm-dd hh:mm:ss (e.g., 2029-10-17 18:39:00)
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Unlimited</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newUnlimited = !isUnlimitedPeriod;
                                  setIsUnlimitedPeriod(newUnlimited);
                                  if (newUnlimited) {
                                    setOverrideAllowedAccessTo('');
                                  } else {
                                    // Restore to current_allowed_access_to when toggling off
                                    if (selectedRequest.current_allowed_access_to) {
                                      const date = new Date(selectedRequest.current_allowed_access_to);
                                      const year = date.getFullYear();
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const hours = String(date.getHours()).padStart(2, '0');
                                      const minutes = String(date.getMinutes()).padStart(2, '0');
                                      const seconds = String(date.getSeconds()).padStart(2, '0');
                                      setOverrideAllowedAccessTo(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);
                                    } else {
                                      setOverrideAllowedAccessTo('');
                                    }
                                  }
                                }}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                                  isUnlimitedPeriod ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    isUnlimitedPeriod ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Response Message (optional)
                    </label>
                    <textarea
                      value={responseMessage}
                      onChange={(e) => setResponseMessage(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder="Add a message to the requester..."
                    />
                  </div>
                </div>
              )}

              {/* View only for already responded */}
              {selectedRequest.status !== 'pending' && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Response</p>
                  <div className="flex items-center gap-3 mb-2">{getStatusBadge(selectedRequest.status)}</div>
                  {selectedRequest.responded_at && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">{formatDate(selectedRequest.responded_at)}</p>
                  )}
                  {selectedRequest.response_message && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{selectedRequest.response_message}</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">Failed to load request details</div>
          )}
        </div>

        {/* Footer with action buttons */}
        {selectedRequest && selectedRequest.status === 'pending' && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-3">
            <button
              onClick={() => {
                setResponseStatus('rejected');
                handleRespond('rejected');
              }}
              disabled={isResponding}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {isResponding && responseStatus === 'rejected' ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-500"></div>
                  <span>Declining...</span>
                </>
              ) : (
                <>
                  <XMarkIcon className="w-4 h-4" />
                  <span>Decline</span>
                </>
              )}
            </button>
            <button
              onClick={() => {
                setResponseStatus('accepted');
                handleRespond('accepted');
              }}
              disabled={isResponding || !!validationError}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {isResponding && responseStatus === 'accepted' ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                  <span>Approving...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  <span>Approve</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AccessRequestResponseModal;

